import Phaser from "phaser";
import type { VehicleConfig } from "../config/vehicles.ts";
import { bakeTexture, OUTLINE, roundedRectPath } from "./bake.ts";
import { css, shade } from "./color.ts";

/**
 * Canvas-baked vehicle art. Each texture is painted once (per size/colour combination) and
 * then shown as a sprite that follows its physics body, so the car is no longer redrawn
 * from scratch every frame.
 */

export interface BodyArt {
  key: string;
  /** Normalised sprite origin that lines up with the chassis body's `position` (its centre
   * of mass, which sits `centerOfMassOffsetY` below the geometric centre). */
  originX: number;
  originY: number;
}

/** Driver head/torso centres in the chassis' position (centre-of-mass) frame. Shared with
 * `Vehicle`, which keeps the physics head here so crash detection matches the artwork. */
export function driverHeadOffsetY(chassisHeight: number): number {
  return -chassisHeight * 1.1;
}
export function driverTorsoOffsetY(chassisHeight: number): number {
  return -chassisHeight * 0.72;
}

export function ensureWheelTexture(scene: Phaser.Scene, radius: number, accent: number): string {
  const key = `wheel-${radius}-${accent.toString(16)}`;
  const size = radius * 2 + 4;
  return bakeTexture(scene, key, size, size, (ctx) => {
    const c = size / 2;
    ctx.translate(c, c);
    const r = radius;

    // Chunky tread knobs poking out past the tire edge.
    const knobs = Math.max(10, Math.round(r * 0.55));
    ctx.fillStyle = css(0x161616);
    for (let i = 0; i < knobs; i++) {
      ctx.save();
      ctx.rotate((i / knobs) * Math.PI * 2);
      roundedRectPath(ctx, r - 5, -3.2, 6.5, 6.4, 1.6);
      ctx.fill();
      ctx.restore();
    }

    const tire = ctx.createRadialGradient(0, 0, r * 0.4, 0, 0, r - 1);
    tire.addColorStop(0, css(0x474747));
    tire.addColorStop(0.7, css(0x2c2c2c));
    tire.addColorStop(1, css(0x141414));
    ctx.fillStyle = tire;
    ctx.beginPath();
    ctx.arc(0, 0, r - 2, 0, Math.PI * 2);
    ctx.fill();

    // Sidewall lettering suggestion: two light arcs (they also make the spin readable).
    ctx.strokeStyle = css(0xbfbfbf, 0.55);
    ctx.lineWidth = Math.max(1.2, r * 0.07);
    for (const start of [0.15, 1.15]) {
      ctx.beginPath();
      ctx.arc(0, 0, r * 0.76, start * Math.PI, (start + 0.45) * Math.PI);
      ctx.stroke();
    }

    // Shiny metal rim.
    const rimR = r * 0.55;
    const rim = ctx.createRadialGradient(-rimR * 0.35, -rimR * 0.35, rimR * 0.1, 0, 0, rimR);
    rim.addColorStop(0, css(0xffffff));
    rim.addColorStop(0.55, css(0xc9ccd1));
    rim.addColorStop(1, css(0x7b8089));
    ctx.fillStyle = rim;
    ctx.beginPath();
    ctx.arc(0, 0, rimR, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = css(OUTLINE);
    ctx.lineWidth = 2;
    ctx.stroke();

    ctx.strokeStyle = css(0x8d929b);
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    ctx.arc(0, 0, rimR * 0.72, 0, Math.PI * 2);
    ctx.stroke();

    // Lug bolts.
    for (let i = 0; i < 5; i++) {
      const a = (i / 5) * Math.PI * 2 - Math.PI / 2;
      const bx = Math.cos(a) * rimR * 0.5;
      const by = Math.sin(a) * rimR * 0.5;
      ctx.fillStyle = css(0x3b3e44);
      ctx.beginPath();
      ctx.arc(bx, by, Math.max(1.6, r * 0.065), 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = css(0xffffff, 0.6);
      ctx.beginPath();
      ctx.arc(bx - 0.5, by - 0.5, Math.max(0.6, r * 0.025), 0, Math.PI * 2);
      ctx.fill();
    }

    // Coloured centre cap.
    ctx.fillStyle = css(accent);
    ctx.beginPath();
    ctx.arc(0, 0, rimR * 0.3, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = css(OUTLINE);
    ctx.lineWidth = 1.5;
    ctx.stroke();
  });
}

/** Bounds of the jeep artwork relative to the chassis' geometric centre. */
const JEEP_BOUNDS = { left: 86, right: 72, top: 74, bottom: 42 };

export function ensureJeepBodyTexture(scene: Phaser.Scene, config: VehicleConfig): BodyArt {
  const { chassisWidth: w, chassisHeight: h, color, accentColor, wheelOffsetX } = config;
  const key = `jeep-body-${w}x${h}-${color.toString(16)}-${accentColor.toString(16)}`;
  const b = JEEP_BOUNDS;
  const texW = b.left + b.right;
  const texH = b.top + b.bottom;

  bakeTexture(scene, key, texW, texH, (ctx) => {
    ctx.translate(b.left, b.top);
    drawJeep(ctx, w, h, color, accentColor, wheelOffsetX, config.centerOfMassOffsetY);
  });

  return {
    key,
    originX: b.left / texW,
    originY: (b.top + config.centerOfMassOffsetY) / texH,
  };
}

function drawJeep(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  color: number,
  accent: number,
  wheelX: number,
  comY: number,
): void {
  const L = -w / 2;
  const R = w / 2;
  const top = -h / 2;
  const bottom = h / 2;
  const outline = css(OUTLINE);

  // --- Behind the body -------------------------------------------------------------------
  drawSpareTire(ctx, L - 9, top + 18, 15);

  // Roll bar: a dark tube with a highlight, leaning back behind the seat.
  const tube = (points: [number, number][]) => {
    for (const [width, style] of [
      [8, outline],
      [3.5, css(0x8d929b)],
    ] as const) {
      ctx.strokeStyle = style;
      ctx.lineWidth = width;
      ctx.beginPath();
      points.forEach(([x, y], i) => (i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)));
      ctx.stroke();
    }
  };
  tube([
    [L + 30, top + 2],
    [L + 34, top - 33],
    [L + 46, top - 35],
  ]);
  tube([
    [L + 12, top + 2],
    [L + 33, top - 30],
  ]);

  // Driver (lower half is hidden by the tub drawn afterwards).
  const headY = driverHeadOffsetY(h) + comY;
  drawDriver(ctx, 0, headY, top);

  // --- Tub ---------------------------------------------------------------------------------
  const bodyPath = () => roundedRectPath(ctx, L, top, w, h, 10);

  bodyPath();
  const grad = ctx.createLinearGradient(0, top, 0, bottom);
  grad.addColorStop(0, css(shade(color, 0.3)));
  grad.addColorStop(0.35, css(color));
  grad.addColorStop(1, css(shade(color, -0.32)));
  ctx.fillStyle = grad;
  ctx.fill();

  ctx.save();
  bodyPath();
  ctx.clip();

  // Woven-canvas texture: faint crosshatch across the paint.
  ctx.strokeStyle = css(0x000000, 0.07);
  ctx.lineWidth = 1;
  for (let x = L - h; x < R + h; x += 4) {
    ctx.beginPath();
    ctx.moveTo(x, top);
    ctx.lineTo(x + h, bottom);
    ctx.moveTo(x + h, top);
    ctx.lineTo(x, bottom);
    ctx.stroke();
  }

  // Rocker panel in the accent colour.
  ctx.fillStyle = css(accent);
  ctx.fillRect(L, bottom - 10, w, 10);
  ctx.fillStyle = css(0xffffff, 0.12);
  ctx.fillRect(L, bottom - 10, w, 2);

  // Glossy highlight along the top edge.
  ctx.fillStyle = css(0xffffff, 0.35);
  roundedRectPath(ctx, L + 7, top + 3, w - 14, 4, 2);
  ctx.fill();
  ctx.restore();

  // Stitched seam just inside the edge.
  ctx.save();
  ctx.setLineDash([3, 3]);
  ctx.strokeStyle = css(0xffffff, 0.4);
  ctx.lineWidth = 1.2;
  roundedRectPath(ctx, L + 4, top + 4, w - 8, h - 18, 7);
  ctx.stroke();
  ctx.restore();

  // Door opening outline + handle.
  ctx.strokeStyle = css(shade(color, -0.45), 0.9);
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(-18, top + 1);
  ctx.lineTo(-18, bottom - 18);
  ctx.quadraticCurveTo(-18, bottom - 13, -12, bottom - 13);
  ctx.lineTo(10, bottom - 13);
  ctx.quadraticCurveTo(15, bottom - 13, 15, bottom - 18);
  ctx.lineTo(15, top + 1);
  ctx.stroke();
  ctx.fillStyle = outline;
  roundedRectPath(ctx, 4, top + 10, 8, 3, 1.5);
  ctx.fill();

  // Hood seam + vent.
  ctx.beginPath();
  ctx.moveTo(25, top + 1);
  ctx.lineTo(25, bottom - 12);
  ctx.stroke();
  ctx.lineWidth = 1.5;
  for (let i = 0; i < 3; i++) {
    ctx.beginPath();
    ctx.moveTo(33 + i * 5, top + 7);
    ctx.lineTo(33 + i * 5, top + 14);
    ctx.stroke();
  }

  bodyPath();
  ctx.strokeStyle = outline;
  ctx.lineWidth = 3;
  ctx.stroke();

  // --- Details in front of the tub -------------------------------------------------------
  // Flared fenders over each wheel.
  for (const fx of [-wheelX, wheelX]) {
    ctx.strokeStyle = outline;
    ctx.lineWidth = 10;
    ctx.beginPath();
    ctx.arc(fx, bottom + 15, 26, Math.PI * 1.13, Math.PI * 1.87);
    ctx.stroke();
    ctx.strokeStyle = css(0x3a3f47);
    ctx.lineWidth = 5;
    ctx.stroke();
    ctx.strokeStyle = css(0xffffff, 0.25);
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(fx, bottom + 15, 27.5, Math.PI * 1.25, Math.PI * 1.6);
    ctx.stroke();
  }

  // Bumpers.
  ctx.fillStyle = outline;
  roundedRectPath(ctx, R - 3, bottom - 17, 11, 13, 3);
  ctx.fill();
  roundedRectPath(ctx, L - 8, bottom - 17, 11, 13, 3);
  ctx.fill();
  ctx.fillStyle = css(0x6b7079);
  roundedRectPath(ctx, R - 1, bottom - 15, 7, 4, 1.5);
  ctx.fill();
  roundedRectPath(ctx, L - 6, bottom - 15, 7, 4, 1.5);
  ctx.fill();

  // Headlight with chrome ring, indicator, tail light.
  const hlX = R - 5;
  const hlY = top + 12;
  const chrome = ctx.createRadialGradient(hlX - 2, hlY - 2, 1, hlX, hlY, 7.5);
  chrome.addColorStop(0, css(0xffffff));
  chrome.addColorStop(1, css(0x8d929b));
  ctx.fillStyle = chrome;
  ctx.beginPath();
  ctx.arc(hlX, hlY, 7.5, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = outline;
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.fillStyle = css(0xfff3b0);
  ctx.beginPath();
  ctx.arc(hlX, hlY, 4.6, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = css(0xffffff, 0.9);
  ctx.beginPath();
  ctx.arc(hlX - 1.5, hlY - 1.5, 1.6, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = css(0xffa22b);
  roundedRectPath(ctx, R - 6, top + 22, 6, 4, 1.5);
  ctx.fill();
  ctx.strokeStyle = outline;
  ctx.lineWidth = 1.2;
  ctx.stroke();

  ctx.fillStyle = css(0xe23b3b);
  roundedRectPath(ctx, L - 1, top + 7, 6, 11, 2);
  ctx.fill();
  ctx.stroke();

  // Windscreen: a slanted frame with a sliver of glass.
  const wsBase = { x: 27, y: top + 1 };
  const wsTop = { x: 20, y: top - 29 };
  ctx.fillStyle = css(0xbfe8ff, 0.75);
  ctx.beginPath();
  ctx.moveTo(wsBase.x - 2, wsBase.y);
  ctx.lineTo(wsTop.x - 2, wsTop.y);
  ctx.lineTo(wsTop.x - 8, wsTop.y + 1);
  ctx.lineTo(wsBase.x - 8, wsBase.y);
  ctx.closePath();
  ctx.fill();
  for (const [width, style] of [
    [6, outline],
    [2.5, css(shade(color, 0.1))],
  ] as const) {
    ctx.strokeStyle = style;
    ctx.lineWidth = width;
    ctx.beginPath();
    ctx.moveTo(wsBase.x, wsBase.y);
    ctx.lineTo(wsTop.x, wsTop.y);
    ctx.stroke();
  }
}

function drawSpareTire(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  ctx.fillStyle = css(OUTLINE);
  roundedRectPath(ctx, x + 4, y - 4, 12, 8, 2);
  ctx.fill();
  ctx.fillStyle = css(0x161616);
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = css(0x353535);
  ctx.beginPath();
  ctx.arc(x, y, r * 0.78, 0, Math.PI * 2);
  ctx.fill();
  const rim = ctx.createRadialGradient(x - 2, y - 2, 1, x, y, r * 0.48);
  rim.addColorStop(0, css(0xffffff));
  rim.addColorStop(1, css(0x7b8089));
  ctx.fillStyle = rim;
  ctx.beginPath();
  ctx.arc(x, y, r * 0.48, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = css(OUTLINE);
  ctx.lineWidth = 1.5;
  ctx.stroke();
}

/** Original cartoon driver: blue cap, round nose, red shirt with blue overalls, one arm on the
 * steering wheel. `headY` is the head centre; `tubTop` is where the body tub hides him. */
function drawDriver(ctx: CanvasRenderingContext2D, x: number, headY: number, tubTop: number): void {
  const outline = css(OUTLINE);
  const skin = 0xf3c48f;

  // Torso.
  const torsoTop = headY + 9;
  ctx.fillStyle = css(0xd9473b);
  roundedRectPath(ctx, x - 10, torsoTop, 21, tubTop - torsoTop + 10, 7);
  ctx.fill();
  ctx.strokeStyle = outline;
  ctx.lineWidth = 2.5;
  ctx.stroke();
  ctx.fillStyle = css(0x3a6fb0);
  roundedRectPath(ctx, x - 7, torsoTop + 7, 15, tubTop - torsoTop + 3, 4);
  ctx.fill();
  ctx.fillStyle = css(0xffd34a);
  ctx.beginPath();
  ctx.arc(x + 4, torsoTop + 10, 1.6, 0, Math.PI * 2);
  ctx.fill();

  // Steering wheel + column.
  const swX = x + 21;
  const swY = tubTop - 6;
  ctx.strokeStyle = outline;
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(swX, swY);
  ctx.lineTo(swX + 5, tubTop + 3);
  ctx.stroke();
  ctx.save();
  ctx.translate(swX, swY);
  ctx.rotate(-0.5);
  ctx.beginPath();
  ctx.ellipse(0, 0, 2.6, 8, 0, 0, Math.PI * 2);
  ctx.lineWidth = 3;
  ctx.stroke();
  ctx.restore();

  // Arm reaching to the wheel: sleeve then hand.
  const shoulder = { x: x + 3, y: torsoTop + 6 };
  const hand = { x: swX - 1, y: swY - 3 };
  for (const [width, style] of [
    [8, outline],
    [4.5, css(0xd9473b)],
  ] as const) {
    ctx.strokeStyle = style;
    ctx.lineWidth = width;
    ctx.beginPath();
    ctx.moveTo(shoulder.x, shoulder.y);
    ctx.quadraticCurveTo(shoulder.x + 6, hand.y + 6, hand.x, hand.y);
    ctx.stroke();
  }
  ctx.fillStyle = css(skin);
  ctx.beginPath();
  ctx.arc(hand.x, hand.y, 3.6, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = outline;
  ctx.lineWidth = 1.8;
  ctx.stroke();

  // Head.
  const headR = 11;
  const face = ctx.createRadialGradient(x + 3, headY - 3, 2, x, headY, headR);
  face.addColorStop(0, css(shade(skin, 0.15)));
  face.addColorStop(1, css(shade(skin, -0.12)));
  ctx.fillStyle = face;
  ctx.beginPath();
  ctx.arc(x, headY, headR, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = outline;
  ctx.lineWidth = 2.5;
  ctx.stroke();

  // Hair tuft at the back, ear.
  ctx.fillStyle = css(0x6b3f22);
  ctx.beginPath();
  ctx.arc(x - 8, headY + 1, 4.5, Math.PI * 0.5, Math.PI * 1.5);
  ctx.fill();
  ctx.fillStyle = css(shade(skin, -0.1));
  ctx.beginPath();
  ctx.arc(x - 3, headY + 2, 3, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = outline;
  ctx.lineWidth = 1.5;
  ctx.stroke();

  // Eye, nose, smile.
  ctx.fillStyle = css(0xffffff);
  ctx.beginPath();
  ctx.ellipse(x + 5.5, headY - 2.5, 2.6, 3.2, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = outline;
  ctx.beginPath();
  ctx.arc(x + 6.5, headY - 2.2, 1.4, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = css(shade(skin, -0.05));
  ctx.beginPath();
  ctx.arc(x + 11, headY + 2, 4.2, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = outline;
  ctx.lineWidth = 1.8;
  ctx.stroke();
  ctx.fillStyle = css(0xffffff, 0.6);
  ctx.beginPath();
  ctx.arc(x + 10, headY + 0.6, 1.2, 0, Math.PI * 2);
  ctx.fill();

  ctx.strokeStyle = outline;
  ctx.lineWidth = 1.6;
  ctx.beginPath();
  ctx.arc(x + 6, headY + 5, 3.2, Math.PI * 0.15, Math.PI * 0.75);
  ctx.stroke();

  // Cap: blue dome with a white front panel and a forward brim.
  ctx.fillStyle = css(0x2f6fd0);
  ctx.beginPath();
  ctx.moveTo(x - 11.5, headY - 3);
  ctx.quadraticCurveTo(x - 11, headY - 16, x + 1, headY - 15.5);
  ctx.quadraticCurveTo(x + 11, headY - 15, x + 11.5, headY - 4);
  ctx.closePath();
  ctx.fill();
  ctx.save();
  ctx.clip();
  ctx.fillStyle = css(0xffffff);
  ctx.fillRect(x + 1, headY - 17, 12, 14);
  ctx.fillStyle = css(0xffffff, 0.3);
  ctx.fillRect(x - 8, headY - 14, 6, 3);
  ctx.restore();
  ctx.strokeStyle = outline;
  ctx.lineWidth = 2.2;
  ctx.beginPath();
  ctx.moveTo(x - 11.5, headY - 3);
  ctx.quadraticCurveTo(x - 11, headY - 16, x + 1, headY - 15.5);
  ctx.quadraticCurveTo(x + 11, headY - 15, x + 11.5, headY - 4);
  ctx.closePath();
  ctx.stroke();
  ctx.fillStyle = css(0x2f6fd0);
  roundedRectPath(ctx, x + 6, headY - 5.5, 13, 4, 2);
  ctx.fill();
  ctx.stroke();
}
